import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Sparkles, Pencil, Trash2, X, Eye, EyeOff, Plus, ImagePlus } from "lucide-react";
import api, { apiError } from "../lib/api";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";

function PostEditor({ initial, onSave, onClose }) {
  const [f, setF] = useState({ title: "", excerpt: "", content: "", status: "draft", tags: [], ...initial });
  const [tagInput, setTagInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const addTag = (val) => {
    const t = (val || "").trim();
    if (!t) return;
    const next = Array.from(new Set([...(f.tags || []), t]));
    setF({ ...f, tags: next });
    setTagInput("");
  };
  const removeTag = (t) => setF({ ...f, tags: (f.tags || []).filter((x) => x !== t) });

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/blog/upload-image", fd);
      setF((prev) => ({ ...prev, content: `${prev.content}${prev.content.endsWith("\n") || !prev.content ? "" : "\n"}\n![Image](${data.url})\n` }));
      toast.success("Image added to the post");
    } catch (e2) { toast.error(apiError(e2)); }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    try { await onSave(f); onClose(); } catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-6 w-full max-w-2xl my-8 space-y-4" data-testid="blog-editor-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{initial?.id ? "Edit post" : "New post"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </div>
        <div><label className="text-xs font-semibold text-slate-500">Title</label>
          <input data-testid="blog-form-title" className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required /></div>
        <div><label className="text-xs font-semibold text-slate-500">Excerpt (shown in the blog list)</label>
          <input data-testid="blog-form-excerpt" className={inputCls} value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} /></div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Tags (comma-separated or press Enter) — these drive the filter chips on the public blog</label>
          <div className="flex flex-wrap items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-2 min-h-[42px]" data-testid="blog-form-tags">
            {(f.tags || []).map((t) => (
              <span key={t} className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-900 text-xs font-semibold px-2 py-0.5 rounded-full">
                {t}
                <button type="button" onClick={() => removeTag(t)} className="hover:text-rose-700" aria-label={`Remove tag ${t}`}><X size={11} /></button>
              </span>
            ))}
            <input
              data-testid="blog-form-tags-input"
              className="flex-1 min-w-[120px] bg-transparent outline-none text-sm py-0.5"
              value={tagInput}
              placeholder={(f.tags || []).length === 0 ? "e.g. Launches, Guides, Case studies, Updates" : "Add another…"}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addTag(tagInput);
                } else if (e.key === "Backspace" && !tagInput && (f.tags || []).length) {
                  removeTag(f.tags[f.tags.length - 1]);
                }
              }}
              onBlur={() => addTag(tagInput)}
            />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {["Launches", "Guides", "Case studies", "Updates", "Opinion", "Behind the scenes"].filter((s) => !(f.tags || []).includes(s)).map((s) => (
              <button key={s} type="button" onClick={() => addTag(s)}
                className="text-[11px] font-semibold text-slate-500 hover:text-emerald-700 border border-dashed border-slate-300 hover:border-emerald-400 rounded-full px-2 py-0.5"
                data-testid={`blog-form-tag-suggest-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                + {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-500">Content (markdown — ## for subheadings, - for bullets, paste a YouTube link on its own line to embed the video)</label>
            <button type="button" data-testid="blog-upload-image-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-800 disabled:opacity-50">
              <ImagePlus size={14} /> {uploading ? "Uploading…" : "Add image"}
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
              data-testid="blog-upload-image-input" onChange={uploadImage} />
          </div>
          <textarea data-testid="blog-form-content" className={`${inputCls} min-h-[320px] font-mono text-[13px]`} value={f.content}
            onChange={(e) => setF({ ...f, content: e.target.value })} required /></div>
        <div className="grid sm:grid-cols-2 gap-3 bg-slate-50 rounded-xl p-3.5 border border-slate-100">
          <div className="sm:col-span-2"><p className="text-xs font-bold text-slate-600">SEO (what Google shows)</p></div>
          <div><label className="text-xs font-semibold text-slate-500">Page title <span className="font-normal text-slate-400">({(f.seo_title || "").length}/60)</span></label>
            <input data-testid="blog-form-seo-title" className={inputCls} maxLength={70} value={f.seo_title || ""}
              onChange={(e) => setF({ ...f, seo_title: e.target.value })} placeholder={f.title || "Defaults to post title"} /></div>
          <div><label className="text-xs font-semibold text-slate-500">Meta description <span className="font-normal text-slate-400">({(f.seo_description || "").length}/155)</span></label>
            <input data-testid="blog-form-seo-description" className={inputCls} maxLength={170} value={f.seo_description || ""}
              onChange={(e) => setF({ ...f, seo_description: e.target.value })} placeholder={f.excerpt || "Defaults to excerpt"} /></div>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="blog-form-status" className={`${inputCls} w-40`} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="draft">Draft (hidden)</option>
            <option value="published">Published</option>
          </select>
          <button data-testid="blog-form-save-btn" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-all">Save post</button>
        </div>
      </form>
    </div>
  );
}

export function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [topic, setTopic] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = (retry = true) => api.get("/admin/blog").then((r) => setPosts(r.data))
    .catch((e) => { if (retry) setTimeout(() => load(false), 1500); else toast.error(apiError(e)); });
  useEffect(() => { load(); }, []);

  const draftWithAI = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/blog/draft", { topic });
      setEditing(data);
      setTopic("");
    } catch (e2) { toast.error(apiError(e2)); }
    setDrafting(false);
  };

  const save = async (f) => {
    if (f.id) await api.put(`/admin/blog/${f.id}`, f);
    else await api.post("/admin/blog", f);
    toast.success(f.status === "published" ? "Post published" : "Draft saved");
    load();
  };

  const togglePublish = async (p) => {
    await api.put(`/admin/blog/${p.id}`, { title: p.title, excerpt: p.excerpt, content: p.content,
      seo_title: p.seo_title || "", seo_description: p.seo_description || "",
      tags: p.tags || [],
      status: p.status === "published" ? "draft" : "published" });
    toast.success(p.status === "published" ? "Post unpublished" : "Post published");
    load();
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/admin/blog/${p.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-3xl space-y-6">
      <form onSubmit={draftWithAI} className="bg-[#0B0F0D] rounded-2xl p-6 text-white">
        <p className="font-display font-bold flex items-center gap-2"><Sparkles size={17} className="text-[#D4AF37]" /> Draft a post with AI</p>
        <p className="text-xs text-gray-400 mt-1.5">Give it a topic — it writes a full post featuring real commands from your dictionary, ready for you to edit and publish.</p>
        <div className="flex gap-3 mt-4">
          <input data-testid="blog-ai-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)}
            placeholder='e.g. "5 shorthand commands for salon owners"'
            className="flex-1 bg-white/10 border border-white/15 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-emerald-500" />
          <button data-testid="blog-ai-draft-btn" disabled={drafting || !topic.trim()}
            className="bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-bold px-5 py-2.5 rounded-xl text-sm disabled:opacity-50 hover:brightness-105 transition-all whitespace-nowrap">
            {drafting ? "Writing…" : "Draft with AI"}
          </button>
        </div>
      </form>

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">{posts.length} post{posts.length === 1 ? "" : "s"}</p>
        <button data-testid="blog-new-post-btn" onClick={() => setEditing({})}
          className="bg-slate-900 text-white text-sm font-bold px-4 py-2.5 rounded-xl flex items-center gap-1.5 hover:bg-slate-800 transition-all"><Plus size={15} /> Write manually</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="blog-posts-list">
        {posts.length === 0 && <p className="text-sm text-slate-400 p-6">No posts yet. Draft one with AI above.</p>}
        {posts.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 px-5 py-4">
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{p.title}</p>
              <p className="text-xs text-slate-400 truncate mt-0.5">{p.excerpt}</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.status}</span>
              <button data-testid={`blog-toggle-${p.slug}`} onClick={() => togglePublish(p)} title={p.status === "published" ? "Unpublish" : "Publish"}
                className="text-slate-400 hover:text-emerald-600 p-1.5">{p.status === "published" ? <EyeOff size={15} /> : <Eye size={15} />}</button>
              <button data-testid={`blog-edit-${p.slug}`} onClick={() => setEditing(p)} className="text-slate-400 hover:text-emerald-600 p-1.5"><Pencil size={15} /></button>
              <button data-testid={`blog-delete-${p.slug}`} onClick={() => del(p)} className="text-slate-400 hover:text-red-500 p-1.5"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing !== null && <PostEditor initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}
